import React from 'react';
import { conditionalObjectItem } from 'BMapsSrc/util/js_utils';
import { Directionality, getDirectionalityString } from './properties_common';

/**
 * Helper function for non-JSX code to get a property tooltip element.
 * @param {string} heading
 * @param {{
 *     label, description, unit, directionality, unitType, range, goodRange, r2,
 * }} propInfo
 * @returns {JSX.Element}
 */
export function getPropertyTooltip(heading, propInfo) {
    return (
        <PropertyTooltip heading={heading} propInfo={propInfo} />
    );
}

/**
 * React component to display a tooltip for a property, given its info.
 * @param {{
 * heading: string,
 * propInfo: {
 *     label, description, unit, directionality, unitType, range, goodRange, r2,
 * }}} props
 * @returns {JSX.Element}
 */

export function PropertyTooltip({ heading, propInfo }) {
    if (!propInfo || Object.keys(propInfo).length === 0) {
        return false;
    }
    const dir = propInfo.directionality && propInfo.directionality !== Directionality.NOT_APPLICABLE
        ? propInfo.directionality
        : null;
    const dirString = dir ? getDirectionalityString(dir, propInfo.unitType, propInfo.unit) : null;
    const attributes = {
        ...conditionalObjectItem(heading, 'heading', heading),
        ...conditionalObjectItem(propInfo.label, 'label', propInfo.label),
        ...conditionalObjectItem(propInfo.description, 'description', propInfo.description),
        ...conditionalObjectItem(propInfo.unit, 'unit', `Unit: ${propInfo.unit}`),
        ...conditionalObjectItem(dirString, 'directionality', `Directionality: ${dirString}`),
        ...conditionalObjectItem(propInfo.range, 'range', `Range: ${propInfo.range}`),
        ...conditionalObjectItem(propInfo.goodRange != null, 'goodRange', `Good Range: ${propInfo.goodRange}`),
        ...conditionalObjectItem(propInfo.r2 != null, 'r2', `Training Confidence (r²): ${propInfo.r2}`),
    };

    return (
        <AttributeListDisplay attributes={attributes} />
    );
}

/**
 * React component to display items in a <p>, separated by <br>.
 * Object keys are only used as React keys, the full attribute text needs to be in Object values.
 * @param {{ attributes }} props
 * @returns {JSX.Element}
 */
export function AttributeListDisplay({ attributes }) {
    const lines = [];
    for (const [key, value] of Object.entries(attributes)) {
        if (typeof value === 'string') {
            lines.push(...(
                value.split('\n').map((line, index) => ({ key, line: index, value: line }))
            ));
        } else {
            lines.push({ key, line: 0, value });
        }
    }
    return (
        <p>
            {lines.map(
                ({ key, line, value }, i) => (
                    <React.Fragment key={`${key}-line-${line}`}>
                        {value}
                        {(i < lines.length - 1) && <br />}
                    </React.Fragment>
                )
            )}
        </p>
    );
}
